import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, AuthContext } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle, Trash2, ShieldAlert, Link2Off, Loader2 } from 'lucide-react';

// Global-Administrator-only confirmation for permanently purging work orders.
// Shows exactly what will be deleted, preserved (with links removed) and flagged
// (financial records) before requiring explicit confirmation.
export const WorkOrderPurgeDialog = ({ open, woIds, onOpenChange, onPurged }) => {
  const { token } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [purging, setPurging] = useState(false);

  const ids = woIds || [];

  const loadPreview = useCallback(async () => {
    setLoading(true); setPreview(null); setConfirmChecked(false);
    try {
      const r = await axios.post(`${API}/work-orders/purge-preview`, { work_order_ids: ids }, authHeaders);
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load purge details');
      onOpenChange(false);
    } finally { setLoading(false); }
  }, [JSON.stringify(ids)]);

  useEffect(() => { if (open && ids.length) loadPreview(); }, [open, loadPreview]);

  const items = preview?.items || [];
  const agg = items.reduce((a, it) => {
    a.photos += it.deletes.photos_and_media;
    a.completions += it.deletes.completion_reports;
    a.reminders += it.deletes.follow_up_reminders;
    a.notifications += it.deletes.customer_notifications;
    a.feedback += it.deletes.customer_feedback;
    if (it.preserves.service_request) a.srs.push(it.preserves.service_request);
    if (it.preserves.property_evaluation) a.pevs.push(it.preserves.property_evaluation);
    it.financial_warnings.forEach((w) => a.warnings.push(`${it.work_order_number}: ${w}`));
    return a;
  }, { photos: 0, completions: 0, reminders: 0, notifications: 0, feedback: 0, srs: [], pevs: [], warnings: [] });

  const deleteLines = [
    agg.photos && `${agg.photos} photo(s) / attachment(s)`,
    agg.completions && `${agg.completions} completion report(s)`,
    agg.reminders && `${agg.reminders} follow-up reminder(s)`,
    agg.notifications && `${agg.notifications} customer notification(s)`,
    agg.feedback && `${agg.feedback} customer feedback record(s)`,
  ].filter(Boolean);

  const doPurge = async () => {
    setPurging(true);
    try {
      const r = await axios.post(`${API}/work-orders/purge`, { work_order_ids: ids, confirm: true }, authHeaders);
      const { failed, failures } = r.data;
      if (failed) toast.error(`${failed} could not be purged: ${(failures || []).map((f) => `${f.work_order_number || f.id} — ${f.error}`).join('; ')}`);
      onOpenChange(false);
      onPurged?.(r.data);  // parent shows the Undo toast + refreshes
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purge failed');
    } finally { setPurging(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !purging && onOpenChange(v)}>
      <DialogContent className="max-w-lg" data-testid="wo-purge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle size={18} /> Permanently purge {ids.length} work order{ids.length === 1 ? '' : 's'}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 py-8 text-sm text-slate-500" data-testid="wo-purge-loading">
            <Loader2 size={16} className="animate-spin" /> Loading what will be deleted…
          </div>
        )}

        {!loading && preview && (
          <div className="space-y-4 text-sm">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Work orders</div>
              <div className="max-h-28 overflow-auto rounded-md border border-slate-200 divide-y divide-slate-100">
                {items.map((it) => (
                  <div key={it.id} className="flex items-center justify-between px-3 py-1.5" data-testid={`wo-purge-item-${it.work_order_number}`}>
                    <span className="font-mono text-xs font-semibold text-[#0E2A47]">{it.work_order_number}</span>
                    <span className="text-xs text-slate-500">{it.status}</span>
                  </div>
                ))}
                {preview.missing?.length > 0 && (
                  <div className="px-3 py-1.5 text-xs text-amber-700">{preview.missing.length} already removed — will be skipped.</div>
                )}
              </div>
            </div>

            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
              <div className="flex items-center gap-1.5 font-semibold text-red-800"><Trash2 size={14} /> Will be permanently deleted</div>
              <ul className="mt-1 list-disc pl-5 text-xs text-red-700">
                <li>The selected work order{ids.length === 1 ? '' : 's'} and {ids.length === 1 ? 'its' : 'their'} data</li>
                {deleteLines.map((l) => <li key={l}>{l}</li>)}
              </ul>
            </div>

            {(agg.srs.length > 0 || agg.pevs.length > 0) && (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="flex items-center gap-1.5 font-semibold text-slate-700"><Link2Off size={14} /> Preserved — links removed</div>
                <ul className="mt-1 list-disc pl-5 text-xs text-slate-600">
                  {agg.srs.length > 0 && <li>Originating service request(s) kept: {agg.srs.join(', ')}</li>}
                  {agg.pevs.length > 0 && <li>Originating property evaluation(s) &amp; findings kept: {agg.pevs.join(', ')}</li>}
                  <li>Customers, locations, personnel and providers are not touched.</li>
                </ul>
              </div>
            )}

            {agg.warnings.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2" data-testid="wo-purge-financial-warning">
                <div className="flex items-center gap-1.5 font-semibold text-amber-800"><ShieldAlert size={14} /> Financial records require separate resolution</div>
                <ul className="mt-1 list-disc pl-5 text-xs text-amber-800">
                  {agg.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            <label className="flex items-start gap-2 pt-1 cursor-pointer">
              <Checkbox checked={confirmChecked} onCheckedChange={(v) => setConfirmChecked(!!v)} data-testid="wo-purge-confirm-check" className="mt-0.5" />
              <span className="text-xs text-slate-700">I understand this permanently deletes the selected work order{ids.length === 1 ? '' : 's'} and cannot be undone.</span>
            </label>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={purging} data-testid="wo-purge-cancel-btn">Cancel</Button>
          <Button onClick={doPurge} disabled={!confirmChecked || purging || loading}
            className="bg-red-600 hover:bg-red-700 text-white" data-testid="wo-purge-confirm-btn">
            {purging ? 'Purging…' : `Purge ${ids.length} work order${ids.length === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WorkOrderPurgeDialog;

// Shows a success toast with an Undo action for the purge grace window. Clicking Undo
// restores the whole batch and calls onRestored() so the caller can refresh its list.
export const showPurgeUndoToast = (res, token, onRestored) => {
  const n = res?.purged ?? (res?.result ? 1 : 0);
  if (!n) return;
  const only = res?.result?.work_order_number || res?.results?.[0]?.work_order_number;
  const label = n === 1 ? `Purged ${only || 'work order'}` : `Purged ${n} work orders`;
  const batchId = res?.batch_id;
  if (!batchId) { toast.success(label); return; }
  toast.success(label, {
    duration: (res?.undo_seconds || 30) * 1000,
    action: {
      label: 'Undo',
      onClick: async () => {
        try {
          const r = await axios.post(`${API}/work-orders/purge/undo`, { batch_id: batchId },
            { headers: { Authorization: `Bearer ${token}` } });
          toast.success(`Restored ${r.data.restored} work order${r.data.restored === 1 ? '' : 's'}`);
          onRestored?.();
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Could not undo — the window may have elapsed');
        }
      },
    },
  });
};
